// ---------------------------------------------------------------------------
// Typed API client for the Ahegao backend (/api/v1)
// ---------------------------------------------------------------------------
// The types come from the Go API's OpenAPI document: schema.gen.ts is
// generated from openapi.json by `mise run gen_api`, and `mise run test`
// fails when either is stale. Never edit them by hand.

import type { components, paths } from "./schema.gen";

type Schemas = components["schemas"];

// ── Types ──────────────────────────────────────────────────────────────────

export type TagRead = Schemas["TagRead"];
export type ActressRead = Schemas["ActressRead"];
export type SourceRead = Schemas["SourceRead"];
export type VideoList = Schemas["VideoList"];
export type VideoDetail = Schemas["VideoDetail"];
export type VideoPage = Schemas["VideoPage"];
export type ReleaseSummary = Schemas["ReleaseSummary"];
export type VideoReleaseRead = Schemas["VideoReleaseRead"];
export type QualityHints = Schemas["QualityHints"];
export type TagWithCount = Schemas["TagWithCount"];
export type ActressWithCount = Schemas["ActressWithCount"];
export type TagListResponse = Schemas["TagListResponse"];
export type ActressListResponse = Schemas["ActressListResponse"];
export type TagUpdate = Body<"/catalog/tags/{id}", "patch">;
export type ApplyPreferencesResult = Schemas["ApplyPreferencesResult"];
export type TrashResponse = Schemas["TrashResult"];
export type BulkTrashResponse = Schemas["BulkTrashResult"];
export type ScraperInfo = Schemas["ScraperInfoBody"];
export type ScrapersResponse = Schemas["ListScrapersOutputBody"];
export type FeedRequest = Body<"/scrape/feed", "post">;
export type FeedMovie = Schemas["Movie"];
export type FeedResponse = Schemas["FeedOutputBody"];
export type ScrapeResponse = Schemas["ScrapeByCodeOutputBody"];
export type RebuildImagesQueuedResponse = Schemas["RebuildImagesOutputBody"];
export type VideoFileRead = Schemas["VideoFileRead"];
export type VideoLibraryRead = Schemas["VideoLibraryRead"];
export type VideoLibraryCreate = Body<"/libraries", "post">;
export type ScanResult = Ok<"/libraries/{id}/scan", "post">;
export type AutoMatchResult = Schemas["AutoMatchResult"];
export type ReorganizeResult = Schemas["ReorganizeResult"];
export type StreamUrlResponse = Schemas["StreamURLBody"];
export type PaginatedFiles = Schemas["ListFilesOutputBody"];
export type TaskProgress = Schemas["TaskProgress"];
export type TaskStatus = Schemas["TaskStatus"];
export type DownloadRead = Schemas["DownloadRead"];
export type PlayStats = Schemas["PlayStats"];
export type FavouriteStudioRead = Schemas["FavouriteStudioRead"];
export type Reaction = Schemas["ReactionBody"]["reaction"];
export type Reactions = Schemas["Reactions"];
export type VideoSort = NonNullable<VideoListParams["sort"]>;
export type QueueDownloadResponse = Schemas["QueueDownloadOutputBody"];
export type AppSettings = Schemas["AppSettingsBody"];
export type MeResponse = Schemas["MeOutputBody"];

export type VideoListParams = Query<"/videos", "get">;
export type CatalogListParams = Query<"/catalog/tags", "get">;
export type VideoStatus = VideoList["status"];
export type TorrentHealth = VideoReleaseRead["health"];
export type JobStatusValue = TaskStatus["status"];
export type DownloadState = DownloadRead["state"];

/** Standardized capability slugs that scrapers declare. */
export type ScraperCapability = "metadata" | "sources" | "feed";

export const VIDEO_STATUS_OPTIONS: Array<{
    value: VideoStatus;
    label: string;
    icon: string;
}> = [
    { value: "new", label: "New", icon: "pi pi-sparkles" },
    { value: "wishlist", label: "Wishlist", icon: "pi pi-bookmark" },
    { value: "owned", label: "Owned", icon: "pi pi-check-circle" },
    { value: "ignored", label: "Ignored", icon: "pi pi-eye-slash" },
];

/** The statuses a click sets: owned comes with a file (a scan or an import). */
export const SETTABLE_STATUS_OPTIONS = VIDEO_STATUS_OPTIONS.filter((o) => o.value !== "owned");

/** States in which torrentd still works on a download. */
export const ACTIVE_DOWNLOAD_STATES: DownloadState[] = [
    "queued",
    "fetching",
    "downloading",
    "seeding",
    "importing",
];

// ── Operations, from the spec ──────────────────────────────────────────────

type Method = "get" | "post" | "put" | "patch" | "delete";
type Path = keyof paths;
/** The methods a path has. */
type MethodOf<P extends Path> = {
    [M in Method]: paths[P][M] extends undefined ? never : M;
}[Method];
type Op<P extends Path, M extends MethodOf<P>> = NonNullable<paths[P][M]>;
type JSONOf<R> = R extends { content: { "application/json": infer B } } ? B : void;
type Success<R> = R extends { 200: infer X } ? X : R extends { 201: infer X } ? X : R extends { 204: infer X } ? X : never;
/** The success response body of an operation. */
type Ok<P extends Path, M extends MethodOf<P>> = Op<P, M> extends { responses: infer R } ? JSONOf<Success<R>> : never;
/** The JSON request body of an operation. */
type Body<P extends Path, M extends MethodOf<P>> =
    Op<P, M> extends { requestBody?: { content: { "application/json": infer B } } } ? B : never;
type Query<P extends Path, M extends MethodOf<P>> =
    Op<P, M> extends { parameters: { query?: infer Q } } ? NonNullable<Q> : never;
type PathParams<P extends Path, M extends MethodOf<P>> =
    Op<P, M> extends { parameters: { path: infer X } } ? X : never;

type Args<P extends Path, M extends MethodOf<P>> = {
    path?: PathParams<P, M>;
    query?: Query<P, M>;
    body?: Body<P, M>;
    /** The request outlives the page (a last report as it closes). */
    keepalive?: boolean;
};

// ── HTTP primitives ────────────────────────────────────────────────────────

const BASE = "/api/v1";

/**
 * Public URL for a video's cover image. The backend's image cache serves
 * locally-cached files directly and lazily fetches from upstream on miss —
 * the frontend doesn't need to know which source the picture came from.
 *
 * Always returns a relative URL so dev (Vite proxy) and prod (Go terminating
 * TLS) both work without ceremony.
 */
export function imageURL(javId: string): string {
    return `${BASE}/images/${encodeURIComponent(javId)}`;
}

class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly detail: unknown,
        message: string,
    ) {
        super(message);
        this.name = "ApiError";
    }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const url = `${BASE}${path}`;
    const res = await fetch(url, {
        headers: {
            "Content-Type": "application/json",
            ...options.headers,
        },
        ...options,
    });

    if (!res.ok) {
        let detail: unknown;
        const text = await res.text();
        try {
            detail = JSON.parse(text);
        } catch {
            detail = text;
        }
        throw new ApiError(
            res.status,
            detail,
            `API ${options.method ?? "GET"} ${url} → ${res.status} ${res.statusText}`,
        );
    }

    // 204 No Content
    if (res.status === 204) return undefined as unknown as T;

    return res.json() as Promise<T>;
}

function buildQuery(params: object = {}): string {
    const q = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== "") {
            q.set(key, String(value));
        }
    }
    const str = q.toString();
    return str ? `?${str}` : "";
}

/** One operation of the spec: the path, its parameters and body are checked. */
function call<P extends Path, M extends MethodOf<P>>(
    method: M,
    path: P,
    args: Args<P, M> = {},
): Promise<Ok<P, M>> {
    const params = (args.path ?? {}) as Record<string, string | number>;
    const url = (path as string).replace(/\{(\w+)\}/g, (_, k: string) =>
        encodeURIComponent(String(params[k])),
    );
    return request<Ok<P, M>>(url + buildQuery(args.query ?? {}), {
        method: method.toUpperCase(),
        body: args.body === undefined ? undefined : JSON.stringify(args.body),
        keepalive: args.keepalive,
    });
}

// ── Video endpoints ────────────────────────────────────────────────────────

export const videosApi = {
    /** Paginated video list with optional search / filter. */
    list(params: VideoListParams = {}) {
        return call("get", "/videos", { query: params });
    },

    /** Full detail for a single video by JAV ID (case-insensitive). */
    get(javId: string) {
        return call("get", "/videos/{javId}", { path: { javId } });
    },

    /** Scrape fresh metadata for a video. */
    scrape(javId: string) {
        return call("post", "/videos/{javId}/scrape", { path: { javId } });
    },

    /** List cached torrent releases for a video. */
    listReleases(javId: string, all = false) {
        return call("get", "/videos/{javId}/releases", { path: { javId }, query: { all } });
    },

    /** Ask torrentd to measure this video's releases now; returns them as they are. */
    probeReleases(javId: string) {
        return call("post", "/videos/{javId}/releases/probe", { path: { javId } });
    },

    /** Search for new torrent releases and cache results. */
    searchReleases(javId: string) {
        return call("post", "/videos/{javId}/releases/search", { path: { javId } });
    },

    updateStatus(javId: string, status: VideoStatus) {
        return call("put", "/videos/{javId}/status", { path: { javId }, body: { status } });
    },

    /** Set or clear the user rating (0–5) for a video. */
    updateRating(javId: string, rating: number | null) {
        return call("patch", "/videos/{javId}/rating", { path: { javId }, body: { rating } });
    },

    /** Delete a video: its files wait a day in .trash (restore), it is ignored and not downloaded again unless forced. */
    rejectAITag(javId: string, tagId: number) {
        return call("delete", "/videos/{javId}/tags/{tagId}/ai", { path: { javId, tagId } });
    },

    similar(javId: string, limit = 12) {
        return call("get", "/videos/{javId}/similar", { path: { javId }, query: { limit } });
    },

    trash(javId: string) {
        return call("delete", "/videos/{javId}/trash", { path: { javId } });
    },

    /** Take a deletion back while the files wait in .trash. */
    restore(javId: string) {
        return call("post", "/videos/{javId}/restore", { path: { javId } });
    },

    /** Bulk-trash all videos matching the given status and max rating. */
    bulkTrash(maxRating: number, status: VideoStatus = "owned") {
        return call("post", "/videos/bulk-trash", { body: { max_rating: maxRating, status } });
    },
};

// ── Scrape endpoints ───────────────────────────────────────────────────────

export const scrapeApi = {
    /** List all registered scraper slugs. Optional filter by capability. */
    listScrapers(capability?: ScraperCapability) {
        return call("get", "/scrape/scrapers", { query: { capability } });
    },

    /**
     * Discovery: a feed-cap scraper performs `scrape_date(date)` or
     * `scrape_search(query)`. Preview by default; pass `ingest: true` to
     * persist the results into videos+sources.
     */
    feed(body: FeedRequest) {
        return call("post", "/scrape/feed", { body });
    },

    /** Scrape specific JAV codes and ingest results. */
    scrapeByCode(body: Body<"/scrape/by-code", "post">) {
        return call("post", "/scrape/by-code", { body });
    },

    /** Queue the cache_images task: every missing cover. */
    rebuildImages() {
        return call("post", "/scrape/rebuild-images");
    },

    /** Queue a task looking up every video without metadata. */
    scrapeMissing(body: Body<"/scrape/missing-metadata", "post">) {
        return call("post", "/scrape/missing-metadata", { body });
    },
};

// ── Catalog endpoints ──────────────────────────────────────────────────────

export const catalogApi = {
    /** List all tags with video counts, sorted by count descending. */
    tags(params: CatalogListParams = {}) {
        return call("get", "/catalog/tags", { query: params });
    },

    /** List all actresses with video counts, sorted by count descending. */
    actresses(params: CatalogListParams = {}) {
        return call("get", "/catalog/actresses", { query: params });
    },

    /** Update tag favourite/hated preferences. */
    updateTag(id: number, body: TagUpdate) {
        return call("patch", "/catalog/tags/{id}", { path: { id }, body });
    },

    /** Apply hated/favourite tag preferences to all videos. */
    applyTagPreferences() {
        return call("post", "/catalog/tags/apply-preferences");
    },
};

// ── Re-export error class ─────────────────────────────────────────────────

export { ApiError };

// ── Library endpoints ──────────────────────────────────────────────────────

export const libraryApi = {
    list() {
        return call("get", "/libraries");
    },

    create(body: VideoLibraryCreate) {
        return call("post", "/libraries", { body });
    },

    delete(id: number) {
        return call("delete", "/libraries/{id}", { path: { id } });
    },

    browse(path = "") {
        return call("get", "/libraries/browse", { query: { path } });
    },

    scan(id: number, background = true) {
        return call("post", "/libraries/{id}/scan", { path: { id }, query: { background } });
    },

    reorganize(id: number, background = true) {
        return call("post", "/libraries/{id}/reorganize", { path: { id }, query: { background } });
    },

    files(id: number, page = 1, perPage = 50) {
        return call("get", "/libraries/{id}/files", { path: { id }, query: { page, per_page: perPage } });
    },

    unmatchedFiles(id: number) {
        return call("get", "/libraries/{id}/unmatched-files", { path: { id } });
    },

    autoMatch(id: number) {
        return call("post", "/libraries/{id}/auto-match", { path: { id } });
    },

    cleanupTrash(id: number) {
        return call("post", "/libraries/{id}/trash/cleanup", { path: { id } });
    },
};

// ── File endpoints ─────────────────────────────────────────────────────────

export const filesApi = {
    streamUrl(fileId: number) {
        return call("get", "/files/{fileId}/stream-url", { path: { fileId } });
    },

    /** Manually associate this file with a JAV id (used by the Identify tab). */
    link(fileId: number, javId: string) {
        return call("post", "/files/{fileId}/link", { path: { fileId }, body: { jav_id: javId } });
    },
};

// ── Task endpoints ─────────────────────────────────────────────────────────

export const tasksApi = {
    list() {
        return call("get", "/tasks");
    },

    get(jobId: number) {
        return call("get", "/tasks/{jobId}", { path: { jobId } });
    },

    abort(jobId: number) {
        return call("delete", "/tasks/{jobId}", { path: { jobId } });
    },

    clearCompleted() {
        return call("post", "/tasks/clear");
    },

    scrapeDate(body: Body<"/tasks/scrape/date", "post">) {
        return call("post", "/tasks/scrape/date", { body });
    },

    scrapeRange(body: Body<"/tasks/scrape/range", "post">) {
        return call("post", "/tasks/scrape/range", { body });
    },

    scrapeFavouriteStudios() {
        return call("post", "/tasks/scrape-favourite-studios");
    },

    /** Search the sources for one studio's releases now. */
    scrapeStudio(studio: string, maxPages?: number) {
        return call("post", "/tasks/scrape-studio", { body: { studio, max_pages: maxPages } });
    },
};

// ── Download endpoints ─────────────────────────────────────────────────────

export const downloadsApi = {
    /** Queue a download; without an infohash the best viable release is taken. */
    /** force: download a video the owner deleted (the API answers 409 without it). */
    queue(javId: string, infohash?: string, force = false) {
        return call("post", "/videos/{javId}/downloads", {
            path: { javId },
            body: { ...(infohash ? { infohash } : {}), ...(force ? { force } : {}) },
        });
    },

    list(state?: DownloadState) {
        return call("get", "/downloads", { query: { state } });
    },

    get(id: number) {
        return call("get", "/downloads/{id}", { path: { id } });
    },

    cancel(id: number) {
        return call("post", "/downloads/{id}/cancel", { path: { id } });
    },
};

// ── Favourite studios ──────────────────────────────────────────────────────

export const studiosApi = {
    list() {
        return call("get", "/studios/favourites");
    },

    /** Add (or, if there, set auto_scrape on) a favourite studio. */
    add(studioCode: string, autoScrape = true) {
        return call("post", "/studios/favourites", { body: { studio_code: studioCode, auto_scrape: autoScrape } });
    },

    setAutoScrape(code: string, autoScrape: boolean) {
        return call("patch", "/studios/favourites/{code}", { path: { code }, body: { auto_scrape: autoScrape } });
    },

    /** Its new releases are downloaded without a click (auto-download). */
    setAutoDownload(code: string, autoDownload: boolean) {
        return call("patch", "/studios/favourites/{code}", { path: { code }, body: { auto_download: autoDownload } });
    },

    remove(code: string) {
        return call("delete", "/studios/favourites/{code}", { path: { code } });
    },
};

// ── Trash ──────────────────────────────────────────────────────────────────

export type TrashedVideo = Schemas["TrashedVideo"];

export const trashApi = {
    /** Deleted videos whose files still wait, the soonest purged first. */
    list() {
        return call("get", "/trash");
    },
    /** Delete the waiting files now: those past their day, or all. */
    purge(all = false) {
        return call("post", "/trash/purge", { body: { all } });
    },
};

// ── Auto-download ──────────────────────────────────────────────────────────

export type AutoDownloadReport = Schemas["Report"];
export type AutoDownloadSettings = Schemas["Settings"];

export const autoDownloadApi = {
    /** The settings, the day's use and what a run would queue now (dry). */
    preview() {
        return call("get", "/auto-download");
    },
    run() {
        return call("post", "/tasks/auto-download");
    },
    /** The settings are the app_config keys auto_download.*. */
    save(s: AutoDownloadSettings) {
        return appApi.patchSettings({
            "auto_download.enabled": s.enabled ? "on" : "off",
            "auto_download.max_per_day": String(s.max_per_day),
            "auto_download.budget_gb_per_day": String(s.budget_gb_per_day),
            "auto_download.min_free_gb": String(s.min_free_gb),
            "auto_download.taste_threshold": String(s.taste_threshold),
            "auto_download.max_age_days": String(s.max_age_days),
        });
    },
};

// ── Taste ──────────────────────────────────────────────────────────────────

export type Recommendation = Schemas["Recommendation"];
export type TasteProfile = Schemas["TasteProfile"];
export type FacetAffinity = Schemas["FacetAffinity"];

export const tasteApi = {
    /** Videos to get by the taste profile, a viable release first; each says why. */
    recommendations(page = 1, perPage = 24) {
        return call("get", "/recommendations", { query: { page, per_page: perPage } });
    },
    profile(limit = 15) {
        return call("get", "/taste", { query: { limit } });
    },
    recompute() {
        return call("post", "/tasks/taste-profile");
    },
};

// ── Reactions ──────────────────────────────────────────────────────────────

/** Like or hate, one tap each; a hate keeps the videos it reaches out of the recommendations. */
export const reactionsApi = {
    list() {
        return call("get", "/reactions");
    },
    video(javId: string, reaction: Reaction) {
        return call("put", "/videos/{javId}/reaction", { path: { javId }, body: { reaction } });
    },
    actress(id: number, reaction: Reaction) {
        return call("put", "/catalog/actresses/{id}/reaction", { path: { id }, body: { reaction } });
    },
    studio(code: string, reaction: Reaction) {
        return call("put", "/studios/{code}/reaction", { path: { code }, body: { reaction } });
    },
    tag(id: number, reaction: Reaction) {
        return call("put", "/catalog/tags/{id}/reaction", { path: { id }, body: { reaction } });
    },
};

// ── Plays ──────────────────────────────────────────────────────────────────

export const playsApi = {
    start(body: Body<"/plays", "post">) {
        return call("post", "/plays", { body });
    },

    report(id: number, body: Body<"/plays/{id}", "patch">, keepalive = false) {
        return call("patch", "/plays/{id}", { path: { id }, body, keepalive });
    },
};

// ── App settings + auth ────────────────────────────────────────────────────

export const appApi = {
    /** Public — always callable. Returns {configured, settings}. */
    getSettings() {
        return call("get", "/settings");
    },

    /**
     * Update one or more settings. The reserved key `auth.password` bcrypts
     * and stores the hash; on the very-first call (when configured is false)
     * a session cookie is set so the caller is logged in immediately.
     */
    patchSettings(settings: Body<"/settings", "patch">) {
        return call("patch", "/settings", { body: settings });
    },
};

export type ConvertibleFile = Schemas["ConvertibleFile"];

/** Files an iPhone cannot play as they are (file-convert). */
export const convertApi = {
    list() {
        return call("get", "/files/convertible");
    },
    convert(fileIds: number[]) {
        return call("post", "/files/convert", { body: { file_ids: fileIds } });
    },
};

export type PairingCode = Schemas["PairingCode"];
export type DeviceRead = Schemas["DeviceRead"];

/** Paired devices (the iOS app): a QR code instead of the password. */
export const devicesApi = {
    pairingCode() {
        return call("post", "/auth/pairing");
    },
    list() {
        return call("get", "/auth/devices");
    },
    revoke(id: number) {
        return call("delete", "/auth/devices/{id}", { path: { id } });
    },
};

export const authApi = {
    login(password: string) {
        return call("post", "/auth/login", { body: { password } });
    },
    logout() {
        return call("post", "/auth/logout");
    },
    me() {
        return call("get", "/auth/me");
    },
};

// ── Triage ─────────────────────────────────────────────────────────────────

export type TriagePage = Schemas["TriagePage"];
export type TriageItem = Schemas["TriageItem"];
export type TriageOrder = NonNullable<Query<"/triage", "get">["order"]>;
export type TriageVerdict = Body<"/videos/{javId}/triage", "put">["verdict"];

/** Keep or delete the owned videos one after another (deleting is videosApi.trash). */
export type BacklogItem = Schemas["BacklogItem"];
export type StudioGroup = Schemas["StudioGroup"];
export type SimilarItem = Schemas["SimilarItem"];
export type FaceMatch = Schemas["FaceMatch"];
export type FaceReviewItem = Schemas["FaceReviewItem"];
export type FacePerson = Schemas["FacePerson"];

/** Discover's triage (discover-triage): the new videos nobody decided on. */
export const backlogApi = {
    queue(query: Query<"/backlog", "get"> = {}) {
        return call("get", "/backlog", { query });
    },
    studios() {
        return call("get", "/backlog/studios");
    },
};

export const facesApi = {
    review(query: Query<"/faces", "get"> = {}) {
        return call("get", "/faces", { query });
    },
    video(javId: string) {
        return call("get", "/videos/{javId}/faces", { path: { javId } });
    },
    verdict(javId: string, actressId: number, verdict: "confirm" | "reject") {
        return call("put", "/videos/{javId}/faces/{actressId}", { path: { javId, actressId }, body: { verdict } });
    },
    run() {
        return call("post", "/tasks/faces");
    },
    people(limit = 120) {
        return call("get", "/faces/people", { query: { limit } });
    },
    namePerson(id: number, name: string) {
        return call("put", "/faces/people/{id}", { path: { id }, body: { name } });
    },
    hidePerson(id: number) {
        return call("delete", "/faces/people/{id}", { path: { id } });
    },
};

export const triageApi = {
    queue(query: Query<"/triage", "get"> = {}) {
        return call("get", "/triage", { query });
    },
    studios() {
        return call("get", "/triage/studios");
    },
    judge(javId: string, verdict: TriageVerdict) {
        return call("put", "/videos/{javId}/triage", { path: { javId }, body: { verdict } });
    },
};
